/**
 * Programación de reportes — issue #70, el criterio que quedó afuera de #47.
 *
 * Lo que faltaba no era pantalla: no existía el endpoint. Ahora sí, y esto es
 * su interfaz. Se programa un reporte (semanal o mensual), se elige a quiénes
 * les llega, y se ve el registro de lo que salió.
 *
 * Se extiende la vista de Reportes en vez de agregar una tercera
 * -----------------------------------------------------------------
 * La deuda del proyecto anota que ya hay **dos** interfaces de exportación,
 * una en Analítica y otra acá, porque se construyeron en paralelo. Este panel
 * va debajo del generador y de `ExportsPanel`, en la misma página: el usuario
 * elige el período arriba, genera el documento del momento, y acá abajo dice
 * "y además, mandámelo todos los lunes".
 *
 * Por qué el destinatario se elige de una LISTA y no se escribe
 * -------------------------------------------------------------
 * La API sólo acepta usuarios del tenant (400 `recipient_not_in_tenant`), y
 * eso no es un capricho del backend: un envío programado a una casilla
 * arbitraria es exfiltración con formulario. Un campo de texto libre invita
 * justamente a escribir el correo personal y recibir un error que se lee como
 * un problema del sistema. Un selector de usuarios hace que la restricción sea
 * evidente en vez de sorpresiva.
 *
 * Por qué el formulario sólo aparece para `admin`
 * -----------------------------------------------
 * `POST /api/v1/reports/schedules` exige `admin` — programar manda datos a
 * OTRAS personas, repetidamente. Y la lista de usuarios
 * (`GET /api/v1/auth/users`) también. Un formulario visible para un analista
 * terminaría siempre en 403: un botón que sólo puede fallar es peor que no
 * tener el botón.
 *
 * Los tipos son locales, no de `schema.d.ts`
 * ------------------------------------------
 * `schema.d.ts` se regenera del OpenAPI y hoy hay tres agentes agregando
 * endpoints en paralelo: regenerarlo desde acá arrastraría rutas a medio
 * construir de los otros. Estas interfaces son el contrato de
 * `services/api/schemas/schedules.py` escrito a mano, y se reemplazan por
 * `components['schemas'][...]` en la próxima regeneración.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import type { ApiError } from '@/api/errors';
import { useKeysetQuery } from '@/api/hooks';
import { authKeys, reportKeys } from '@/api/keys';
import type { User } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import type { MessageKey } from '@/i18n/messages';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';

const PAGE_SIZE = 10;
const USERS_PAGE = 50;

const PERIODS = ['last_week', 'last_month', 'last_30d'] as const;
type SchedulePeriod = (typeof PERIODS)[number];

const CADENCES = ['weekly', 'monthly'] as const;
type Cadence = (typeof CADENCES)[number];

/** ISO: 1 = lunes … 7 = domingo, igual que `report_schedule.weekday`. */
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
type Weekday = (typeof WEEKDAYS)[number];

/**
 * Días del mes que se ofrecen. Topeado en 28 igual que el CHECK de la base: el
 * 29, 30 y 31 no existen todos los meses, y cualquier regla de arrastre
 * sorprende a quien la configuró.
 */
const MONTHDAYS = Array.from({ length: 28 }, (_, index) => index + 1);

interface Schedule {
  id: number;
  kind: string;
  period: string;
  cadence: string;
  recipients: string[];
  locale: string;
  timezone: string;
  hour: number;
  weekday: number | null;
  monthday: number | null;
  is_active: boolean;
  next_run_at: string;
  last_run_at: string | null;
  created_at: string;
}

interface Run {
  id: number;
  schedule_id: number | null;
  period: string;
  period_start: string;
  period_end: string;
  trigger: string;
  status: string;
  recipients: string[];
  byte_size: number | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

const PERIOD_KEYS: Record<SchedulePeriod, MessageKey> = {
  last_week: 'reports.schedule.period.lastWeek',
  last_month: 'reports.schedule.period.lastMonth',
  last_30d: 'reports.schedule.period.last30d',
};

const CADENCE_KEYS: Record<Cadence, MessageKey> = {
  weekly: 'reports.schedule.cadence.weekly',
  monthly: 'reports.schedule.cadence.monthly',
};

const WEEKDAY_KEYS: Record<Weekday, MessageKey> = {
  1: 'reports.schedule.weekday.1',
  2: 'reports.schedule.weekday.2',
  3: 'reports.schedule.weekday.3',
  4: 'reports.schedule.weekday.4',
  5: 'reports.schedule.weekday.5',
  6: 'reports.schedule.weekday.6',
  7: 'reports.schedule.weekday.7',
};

const RUN_STATUS_KEYS: Record<string, MessageKey> = {
  queued: 'reports.schedule.run.queued',
  running: 'reports.schedule.run.running',
  sent: 'reports.schedule.run.sent',
  failed: 'reports.schedule.run.failed',
};

const RUN_STATUS_TONES: Record<string, Tone> = {
  queued: 'progress',
  running: 'progress',
  sent: 'verified',
  failed: 'critical',
};

export function SchedulesPanel(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const isAdmin = can('admin');

  const [period, setPeriod] = useState<SchedulePeriod>('last_week');
  const [cadence, setCadence] = useState<Cadence>('weekly');
  const [weekday, setWeekday] = useState(1);
  const [monthday, setMonthday] = useState(1);
  const [hour, setHour] = useState(7);
  const [recipients, setRecipients] = useState<string[]>([]);

  const schedules = useKeysetQuery<Schedule>(
    reportKeys.schedules(PAGE_SIZE),
    '/api/v1/reports/schedules',
    undefined,
    { limit: PAGE_SIZE },
  );

  const runs = useKeysetQuery<Run>(
    reportKeys.runs(PAGE_SIZE),
    '/api/v1/reports/runs',
    undefined,
    { limit: PAGE_SIZE },
  );

  // La lista de usuarios sólo se pide si el rol la puede leer. Pedirla igual
  // dejaría un 403 en la consola en cada carga de la página para un analista.
  const users = useKeysetQuery<User>(authKeys.users(USERS_PAGE), '/api/v1/auth/users', undefined, {
    limit: USERS_PAGE,
    enabled: isAdmin,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: reportKeys.all });
  };

  const create = useMutation<Schedule, ApiError>({
    mutationFn: () =>
      api.post<Schedule>('/api/v1/reports/schedules', {
        period,
        cadence,
        recipients,
        hour,
        // El día que no corresponde a la cadencia se omite: la API rechaza
        // una fila con los dos, porque no tendría una lectura única.
        ...(cadence === 'weekly' ? { weekday } : { monthday }),
        // La zona del navegador, que es la del laboratorio que lo configura.
        // Sin ella, «los lunes a las 7» sería un momento distinto del día.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    onSuccess: () => {
      setRecipients([]);
      invalidate();
    },
  });

  const toggle = useMutation<Schedule, ApiError, Schedule>({
    mutationFn: (schedule) =>
      api.patch<Schedule>(`/api/v1/reports/schedules/${schedule.id}`, {
        is_active: !schedule.is_active,
      }),
    onSuccess: invalidate,
  });

  const remove = useMutation<unknown, ApiError, Schedule>({
    mutationFn: (schedule) => api.delete(`/api/v1/reports/schedules/${schedule.id}`),
    onSuccess: invalidate,
  });

  const runNow = useMutation<Run, ApiError, Schedule>({
    mutationFn: (schedule) => api.post<Run>(`/api/v1/reports/schedules/${schedule.id}/run`, {}),
    onSuccess: invalidate,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (recipients.length === 0) return;
    create.mutate();
  };

  const toggleRecipient = (email: string): void => {
    setRecipients((current) =>
      current.includes(email) ? current.filter((item) => item !== email) : [...current, email],
    );
  };

  const { query } = schedules;

  return (
    <Card className="print:hidden">
      <CardHeader
        title={t('reports.schedule.title')}
        description={t('reports.schedule.description')}
      />

      {isAdmin ? (
        <CardBody className="border-b border-border">
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                <span className="block text-ink-muted">{t('reports.schedule.form.period')}</span>
                <select
                  value={period}
                  onChange={(event) => {
                    setPeriod(event.target.value as SchedulePeriod);
                  }}
                  className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                >
                  {PERIODS.map((option) => (
                    <option key={option} value={option}>
                      {t(PERIOD_KEYS[option])}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <span className="block text-ink-muted">{t('reports.schedule.form.cadence')}</span>
                <select
                  value={cadence}
                  onChange={(event) => {
                    setCadence(event.target.value as Cadence);
                  }}
                  className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                >
                  {CADENCES.map((option) => (
                    <option key={option} value={option}>
                      {t(CADENCE_KEYS[option])}
                    </option>
                  ))}
                </select>
              </label>

              {cadence === 'weekly' ? (
                <label className="text-sm">
                  <span className="block text-ink-muted">{t('reports.schedule.form.weekday')}</span>
                  <select
                    value={weekday}
                    onChange={(event) => {
                      setWeekday(Number(event.target.value));
                    }}
                    className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                  >
                    {WEEKDAYS.map((day) => (
                      <option key={day} value={day}>
                        {t(WEEKDAY_KEYS[day])}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <label className="text-sm">
                  <span className="block text-ink-muted">
                    {t('reports.schedule.form.monthday')}
                  </span>
                  <select
                    value={monthday}
                    onChange={(event) => {
                      setMonthday(Number(event.target.value));
                    }}
                    className="mt-1 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                  >
                    {MONTHDAYS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="text-sm">
                <span className="block text-ink-muted">{t('reports.schedule.form.hour')}</span>
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={hour}
                  onChange={(event) => {
                    setHour(Number(event.target.value));
                  }}
                  className="mt-1 w-20 rounded border border-border-strong bg-surface px-3 py-1.5 text-sm text-ink"
                />
              </label>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm text-ink-muted">
                {t('reports.schedule.form.recipients')}
              </legend>
              {/* La restricción de la API, dicha antes de que el usuario la
                  choque: sólo usuarios de la marca. */}
              <p className="text-xs text-ink-subtle">{t('reports.schedule.form.recipientsHint')}</p>
              {users.query.isPending && <LoadingState rows={2} />}
              {users.items.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {users.items
                    .filter((user) => user.is_active)
                    .map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={recipients.includes(user.email)}
                          onChange={() => {
                            toggleRecipient(user.email);
                          }}
                        />
                        <span>{user.full_name ?? user.email}</span>
                        <span className="text-xs text-ink-subtle">{user.email}</span>
                      </label>
                    ))}
                </div>
              )}
            </fieldset>

            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={create.isPending || recipients.length === 0}
            >
              {create.isPending
                ? t('reports.schedule.form.saving')
                : t('reports.schedule.form.submit')}
            </Button>

            {create.isError && <ErrorState className="mt-3" error={create.error} />}
          </form>
        </CardBody>
      ) : (
        <CardBody className="border-b border-border">
          <p className="text-sm text-ink-muted">{t('reports.schedule.adminOnly')}</p>
        </CardBody>
      )}

      {query.isPending && <LoadingState rows={3} />}

      {query.isError && (
        <CardBody>
          <ErrorState
            error={query.error}
            onRetry={() => {
              void query.refetch();
            }}
          />
        </CardBody>
      )}

      {!query.isPending && !query.isError && schedules.items.length === 0 && (
        <EmptyState title={t('reports.schedule.empty')} detail={t('reports.schedule.emptyHint')} />
      )}

      {schedules.items.length > 0 && (
        <Table caption={t('reports.schedule.description')}>
          <thead>
            <tr>
              <Th>{t('reports.schedule.column.what')}</Th>
              <Th>{t('reports.schedule.column.when')}</Th>
              <Th>{t('reports.schedule.column.recipients')}</Th>
              <Th>{t('reports.schedule.column.next')}</Th>
              <Th>{t('reports.schedule.column.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {schedules.items.map((schedule) => (
              <tr key={schedule.id}>
                <Td className="font-medium">{periodLabel(schedule.period, t)}</Td>
                <Td className="text-ink-muted">{scheduleWhen(schedule, t)}</Td>
                {/* Quiénes reciben la analítica de la marca. Es el dato por el
                    que se audita este recurso, así que se muestra entero. */}
                <Td className="text-ink-muted">{schedule.recipients.join(', ')}</Td>
                <Td className="text-ink-muted">
                  {schedule.is_active ? (
                    fmt.dateTime(schedule.next_run_at)
                  ) : (
                    <Badge tone="neutral">{t('reports.schedule.paused')}</Badge>
                  )}
                </Td>
                <Td>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        runNow.mutate(schedule);
                      }}
                      disabled={runNow.isPending}
                    >
                      {t('reports.schedule.runNow')}
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            toggle.mutate(schedule);
                          }}
                          disabled={toggle.isPending}
                        >
                          {schedule.is_active
                            ? t('reports.schedule.pause')
                            : t('reports.schedule.resume')}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            remove.mutate(schedule);
                          }}
                          disabled={remove.isPending}
                        >
                          {t('reports.schedule.delete')}
                        </Button>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {(runNow.isError || toggle.isError || remove.isError) && (
        <CardBody>
          <ErrorState error={runNow.error ?? toggle.error ?? remove.error} />
        </CardBody>
      )}

      {/* El registro de envíos. Es lo que un auditor pide: cuándo salió, a
          quiénes y con qué resultado. */}
      {runs.items.length > 0 && (
        <>
          <CardBody className="border-t border-border pb-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-ink">
                  {t('reports.schedule.runs.title')}
                </h3>
                <p className="text-xs text-ink-subtle">
                  {t('reports.schedule.runs.description')}
                </p>
              </div>
              {/* Botón y no sondeo automático: una corrida tarda segundos y
                  se mira una vez. Un `refetchInterval` permanente cuesta una
                  consulta cada dos segundos a todo el que deje la pestaña
                  abierta, que es lo que este panel NO necesita. */}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  void runs.query.refetch();
                }}
                disabled={runs.query.isFetching}
              >
                {runs.query.isFetching ? t('state.refreshing') : t('reports.schedule.runs.refresh')}
              </Button>
            </div>
          </CardBody>
          <Table caption={t('reports.schedule.runs.title')}>
            <thead>
              <tr>
                <Th>{t('reports.schedule.runs.column.period')}</Th>
                <Th>{t('reports.schedule.runs.column.status')}</Th>
                <Th>{t('reports.schedule.runs.column.recipients')}</Th>
                <Th>{t('reports.schedule.runs.column.trigger')}</Th>
                <Th>{t('reports.schedule.runs.column.when')}</Th>
              </tr>
            </thead>
            <tbody>
              {runs.items.map((run) => (
                <tr key={run.id}>
                  <Td className="text-ink-muted">
                    {run.period_start} – {run.period_end}
                  </Td>
                  <Td>
                    <Badge tone={RUN_STATUS_TONES[run.status] ?? 'neutral'}>
                      {runStatusLabel(run.status, t)}
                    </Badge>
                  </Td>
                  <Td className="text-ink-muted">{run.recipients.join(', ') || '—'}</Td>
                  <Td className="text-ink-muted">
                    {run.trigger === 'manual'
                      ? t('reports.schedule.runs.manual')
                      : t('reports.schedule.runs.scheduled')}
                  </Td>
                  <Td className="text-ink-muted">{fmt.dateTime(run.finished_at ?? run.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CardBody className="flex items-center justify-center border-t border-border">
            {runs.hasMore ? (
              <Button size="sm" onClick={runs.loadMore} disabled={runs.isLoadingMore}>
                {runs.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
              </Button>
            ) : (
              <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
            )}
          </CardBody>
        </>
      )}
    </Card>
  );
}

/**
 * `weekday` llega como `number | null` del JSON. El type guard es lo que
 * convierte «la API mandó un 9» en «se muestra sólo la hora» en vez de en un
 * `undefined` que atraviesa el traductor y termina como texto vacío.
 */
function isWeekday(value: number | null): value is Weekday {
  return value !== null && (WEEKDAYS as readonly number[]).includes(value);
}

/** Un `period` que la API agregue después se muestra como viene, sin romper. */
function periodLabel(period: string, t: (key: MessageKey) => string): string {
  return (PERIODS as readonly string[]).includes(period)
    ? t(PERIOD_KEYS[period as SchedulePeriod])
    : period;
}

function runStatusLabel(status: string, t: (key: MessageKey) => string): string {
  const key = RUN_STATUS_KEYS[status];
  return key === undefined ? status : t(key);
}

/**
 * «Los lunes a las 07:00» o «el 1 de cada mes a las 07:00».
 *
 * La hora se muestra con la zona guardada, no traducida a la del navegador:
 * la programación se configuró para el horario del laboratorio, y mostrarla
 * en otro huso haría que el número no coincida con el que se eligió.
 */
function scheduleWhen(schedule: Schedule, t: (key: MessageKey) => string): string {
  const hora = `${String(schedule.hour).padStart(2, '0')}:00`;
  if (schedule.cadence === 'weekly' && isWeekday(schedule.weekday)) {
    return `${t(WEEKDAY_KEYS[schedule.weekday])} · ${hora} ${schedule.timezone}`;
  }
  if (schedule.cadence === 'monthly' && schedule.monthday !== null) {
    return `${t('reports.schedule.monthlyOn')} ${schedule.monthday} · ${hora} ${schedule.timezone}`;
  }
  return `${hora} ${schedule.timezone}`;
}